import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Printer, Sparkles } from "lucide-react";

export function GiftCardSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("pending");
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setStatus(r.data.payment_status);
        if (r.data.payment_status === "paid") {
          // Fetch the gift card code from orders
          const orders = await api.get("/admin/orders").catch(()=>null);
          // Fallback: parse session metadata via Stripe not needed — we have session_id
        }
      } catch (e) { console.warn("Statut paiement indisponible, nouvel essai…", e?.message); }
      if (attempts++ < 12 && status !== "paid") setTimeout(poll, 2500);
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="gift-success-page" className="max-w-2xl mx-auto px-6 md:px-12 py-20 text-center">
      {status === "paid" ? (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#7B8A72] mb-6"/>
          <h1 className="font-serif text-5xl mb-4">Ta carte cadeau est prête</h1>
          <p className="font-hand text-2xl text-[#A3876A] mb-8">Un email t'attend</p>
          <p className="text-[#4A4036] mb-10 leading-relaxed">
            Je t'ai envoyé la carte cadeau à imprimer par email. Tu peux y accéder à tout moment depuis ce lien.
          </p>
          <Link to="/" className="btn-outline">Retour à l'accueil</Link>
        </>
      ) : (
        <>
          <h1 className="font-serif text-4xl mb-4">Confirmation en cours…</h1>
          <p className="text-[#4A4036]">Merci de patienter quelques instants.</p>
        </>
      )}
    </div>
  );
}

export function GiftCardPrint() {
  const { code } = useParams();
  const [g, setG] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/gift-cards/${code}`)
      .then(r => setG(r.data))
      .catch(e => setError(e.response?.data?.detail || "Carte introuvable"));
  }, [code]);

  if (error) return <div className="max-w-xl mx-auto px-6 py-20 text-center"><h1 className="font-serif text-3xl mb-4">Oups</h1><p>{error}</p></div>;
  if (!g) return <div className="max-w-xl mx-auto px-6 py-20 text-center">Chargement…</div>;

  const expiresLabel = new Date(g.expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="gift-print-page" className="min-h-screen bg-[#FDFBF7] py-10 print:py-0 print:bg-white">
      {/* Print controls — hidden on print */}
      <div className="max-w-3xl mx-auto px-6 mb-8 flex items-center justify-between print:hidden">
        <Link to="/" className="text-sm text-[#7C7064] hover:text-[#A3876A]">← Retour à l'accueil</Link>
        <button data-testid="print-btn" onClick={()=>window.print()} className="btn-primary"><Printer size={14}/> Imprimer</button>
      </div>

      {/* The card — A5 friendly */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="relative bg-[#F5F0E6] p-12 md:p-16 border border-[#C5A880] paper-grain print:border-black">
          {/* Decorative corner accents */}
          <div className="absolute top-6 left-6 w-12 h-12 border-t border-l border-[#A3876A]"></div>
          <div className="absolute top-6 right-6 w-12 h-12 border-t border-r border-[#A3876A]"></div>
          <div className="absolute bottom-6 left-6 w-12 h-12 border-b border-l border-[#A3876A]"></div>
          <div className="absolute bottom-6 right-6 w-12 h-12 border-b border-r border-[#A3876A]"></div>

          <div className="text-center mb-10">
            <p className="font-hand text-3xl text-[#A3876A] mb-3">Un cadeau pour toi</p>
            <Sparkles size={20} className="mx-auto text-[#A3876A]"/>
          </div>

          <div className="text-center mb-10">
            <p className="text-sm tracking-[0.3em] uppercase text-[#7C7064] mb-4">Carte cadeau</p>
            <h1 className="font-serif text-4xl md:text-5xl text-[#2C2621] leading-tight mb-3">
              {g.product_name}
            </h1>
            {g.recipient_name && (
              <p className="font-hand text-3xl text-[#A3876A] mt-6">Pour {g.recipient_name}</p>
            )}
          </div>

          {g.dedication && (
            <div className="max-w-lg mx-auto mb-10 py-6 border-y border-[#DCCBBA]">
              <p className="editorial-quote text-center text-xl text-[#4A4036] italic">« {g.dedication} »</p>
            </div>
          )}

          <div className="text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-[#7C7064] mb-3">Code à saisir sur le site</p>
            <p className="font-serif text-3xl md:text-4xl tracking-[0.4em] text-[#2C2621] bg-[#FDFBF7] py-4 px-6 inline-block border border-[#DCCBBA]">
              {g.code}
            </p>
            <p className="text-sm text-[#7C7064] mt-6">
              Valable jusqu'au <strong>{expiresLabel}</strong>
            </p>
            <p className="text-xs text-[#7C7064] mt-2">
              lesrdvdelecriture.com — Les RDV de l'écriture
            </p>
          </div>

          <div className="text-center mt-10">
            <p className="font-hand text-2xl text-[#A3876A]">Prends soin de toi ✦</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-8 print:hidden text-center">
          <p className="text-sm text-[#7C7064]">
            Astuce : imprime cette carte sur un joli papier crème, puis plie-la en deux avant de la glisser dans une enveloppe.
          </p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 20mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
